<script lang="ts">
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.1.0
 * @copyright  Copyright (C) 2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2024-08-23 (1.0.0) (jhoward)
 *    - Refactor textual default section from <textual-menu> to <default-textual-section>.
 *
 *  2024-08-26 (1.1.0) (jhoward)
 *    - Handle selected menu items via store in <default-textual-section>.
 *    - Add scoped CSS in <default-textual-section>.
 *    - Customize item grid in <default-textual-section> based on rows in each item.
 */

import {defineComponent, PropType} from 'vue'
import MenuTagLegend from "../../menu-tag-legend.vue";
import MenuTag from "../../menu-tag.vue";
import {Menu, MenuItem, MenuSection} from "../../defs";
import {computedIsScheduled, isNormalizedScheduleActive} from "../../../../helpers/menu-sections";
import MenuVersion from "../../menu-version.vue";
import DefaultItem from "../../item/textual/defaultTextualItem.vue";

export default defineComponent({
  name: "defaultTextualSection",
  components: {DefaultItem, MenuVersion, MenuTag, MenuTagLegend},
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    items: {
      type: Array as PropType<MenuItem[]>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu | null>,
      required: true
    },
  },
  data() {
    return {
      maxItemRows: 1,
    };
  },
  computed: {
    isScheduled: computedIsScheduled,
    isEmpty(): boolean {
      if (this.section.menu_items && this.section.menu_items.length) {
        for (let i = 0, l = this.section.menu_items?.length || 0; i < l; i++) {
          if (0 === this.section.menu_items[i].normalizedSchedules?.length) {
            return false;
          }
          for (let ii = 0, ll = this.section.menu_items[i].normalizedSchedules?.length || 0; ii < ll; ii++) {
            if (isNormalizedScheduleActive(this.section.menu_items[i].normalizedSchedules[ii], this.$store.state.currentDateTimeUtc, this.$store.state.currentDayOfWeek, this.$store.state.currentTimeOfDay)) {
              return false;
            }
          }
        }
      }

      return true;
    },
  },
  methods: {
    sectionContentClasses(section: MenuSection): { [key: string]: boolean } {
      const output: { [key: string]: boolean } = {};
      if (section.menu_items) {
        output['item-count-' + section.menu_items.length] = true;
      }
      return output;
    },
    handleItemRows(rows: number) {
      if (rows > this.maxItemRows) {
        this.maxItemRows = rows;
      }
    },
  },
})
</script>

<template>
  <div v-show="isScheduled && !isEmpty"
       :data-section-id="section.ms_id"
       class="body-container page-links no-images show-all-items textual_menu--section">
    <div class="page-link-group-hash" :id="section.ms_id + '-MS'"></div>
    <h2 class="page-links" tabindex="-1">{{ section.ms_title }}</h2>
    <h3 v-if="section.ms_subtitle.length" v-html="section.ms_subtitle"
        class="textual_menu--section_subtitle"></h3>

    <div v-if="section.ms_content_area_top.length" class="page-link-group-copy">
      <div class="viewport" v-html="section.ms_content_area_top"></div>
    </div>

    <div class="viewport" :class="sectionContentClasses(section)">
      <div class="body-container-scroller">

        <default-item v-for="(item, itemIndex) in items" :key="'item-' + item.mi_id" :item="item"
                      :item-index="itemIndex" :menu="menu" @rows="handleItemRows"
                      :style="{'--item-rows': maxItemRows}"></default-item>

      </div>
      <a class="page-link-view-more btn" :href="'#' + section.ms_id + '-MS'">VIEW MORE</a>
    </div>

    <div v-if="section.ms_content_area_bottom.length || section.menu_items?.length"
         class="page-link-group-copy">
      <menu-tag-legend v-if="(section.menu_items || []).length" :items="section.menu_items || []"
                       menu-type="textual"></menu-tag-legend>
      <div v-if="section.ms_content_area_bottom.length" v-html="section.ms_content_area_bottom"
           class="viewport"></div>
    </div>

    <menu-version v-if="section.ms_menu_version_override || menu?.menu_version"
                  :version="section.ms_menu_version_override || menu?.menu_version || ''"></menu-version>

  </div>
</template>

<style scoped>
h2.page-links {
  font-size: 1.3rem !important;
}

.body-container.textual_menu--section > .viewport::before {
  display: none;
}

.body-container.textual_menu--section > .viewport {
  padding-top: 24px;
}
</style>